import { z } from "zod";
import {
  ACCOUNT_NAME_MAX_LENGTH,
  ACCOUNT_NAME_REQUIRED_MESSAGE,
  ACCOUNT_NUMBER_INVALID_MESSAGE,
  ACCOUNT_NUMBER_MAX_LENGTH,
  ACCOUNT_NUMBER_MIN_LENGTH,
  ACCOUNT_NUMBER_REQUIRED_MESSAGE,
  BANK_NAME_MAX_LENGTH,
  BANK_NAME_REQUIRED_MESSAGE,
} from "./funds.js";
import { payeeLabelSchema } from "./payee-label.js";

/**
 * 🔴🔴 US-E5.F2-0598-S02 (@UC0030 · QĐ người dùng 31/08/2026) — TK NHẬN TIỀN **CẤP NHÓM**.
 *
 * ═══ VÌ SAO FILE NÀY TỒN TẠI THAY VÌ SỬA `funds.ts` ═══
 * Thông tin ngân hàng **di dời** từ quỹ lên nhóm (`Group.bankAccount`). Nhưng `funds.ts` giữ nguyên
 * schema cũ vì **`apps/flutter-web` đang deploy** còn gọi `PUT /v1/funds/:id/bank-account` — S04 đổi
 * RUỘT ba endpoint quỹ để chúng đọc/ghi xuống nhóm, **không đổi hợp đồng** của chúng.
 *
 * ⇒ Hai bề mặt HTTP, **một dữ liệu**. File này khai bề mặt MỚI (cấp nhóm); `funds.ts` khai bề mặt CŨ.
 * Hằng validate **import lại từ `funds.ts`**, KHÔNG chép: hai bản số 6–19 sẽ trôi khỏi nhau.
 *
 * 🔴 KHÔNG có `bankBin` trong body cấp nhóm — **QĐ-7**: thiết kế (`591:22639`) cho ô "Ngân hàng" là
 * **nhập tự do** ("Nhập tên ngân hàng"), không phải ô chọn. Nên đường mobile chỉ ghi `bankName`.
 * `bankBin` vẫn TỒN TẠI trong dữ liệu lưu (`storedGroupBankAccountSchema`) vì flutter-web ghi được
 * qua đường cũ, và merge phải GIỮ nó — ghi đè mất `bankBin` của người ta là mất dữ liệu lặng lẽ.
 */

/** Giới hạn tên tệp ảnh QR hiển thị lại ở màn Cài đặt (thiết kế: `qr_code_nguyen_van_an.png`). */
export const QR_FILE_NAME_MAX_LENGTH = 200;

/**
 * Body cho `PUT /v1/groups/:id/bank-account`.
 *
 * MỌI field OPTIONAL — **QĐ-3**: ảnh QR mới là thứ quyết định "đã cấu hình", text chỉ là bản sao chép
 * tay cho người không quét được QR. Body rỗng hợp lệ (client chỉ gửi ô vừa sửa).
 *
 * 🔴 **"OPTIONAL" ≠ "được rỗng"** (vá review L-2 + H-4). Vắng khoá ⇒ giữ nguyên giá trị cũ; có khoá
 * thì phải có nội dung. Trước bản vá, `bankName` thiếu `.min(1)` nên `""` lọt vào jsonb trong khi
 * hai ô kia trả 400 — ba ô cạnh nhau, hai luật khác nhau, không vì lý do nào.
 * ⇒ **KHÔNG có đường XOÁ TRẮNG một ô qua endpoint này**, và đó là chủ ý: xoá số tài khoản trong khi
 * ảnh QR vẫn trỏ đúng tài khoản đó chỉ tạo ra một màn hình nửa vời. Docblock cũ ở đây khai "body
 * rỗng hợp lệ (người dùng xoá trắng một ô)" — **sai**, `.min(1)` chặn từ đầu; client nay nói thẳng
 * điều đó cho người dùng thay vì nuốt lặng thao tác.
 *
 * 🔴 `.strict()` — trạng thái sai KHÔNG BIỂU DIỄN ĐƯỢC (`CLAUDE.md §7`): gửi `bankBin` hay một key lạ
 * qua đường này ⇒ **400 ở biên, 0 row chạm DB**. Đây là thứ chặn ghi rác vào jsonb.
 */
export const groupBankAccountSchema = z
  .object({
    accountNumber: z
      .string()
      .trim()
      .min(1, ACCOUNT_NUMBER_REQUIRED_MESSAGE)
      .regex(
        new RegExp(`^\\d{${ACCOUNT_NUMBER_MIN_LENGTH},${ACCOUNT_NUMBER_MAX_LENGTH}}$`),
        ACCOUNT_NUMBER_INVALID_MESSAGE,
      )
      .optional(),
    // NAME-SANITIZE-PAYEE-FIELDS (@UC0030 @UC0055): gộp khoảng trắng + chặn ký tự không in được (`payee-label.ts`).
    accountName: payeeLabelSchema((s) => s.min(1, ACCOUNT_NAME_REQUIRED_MESSAGE).max(ACCOUNT_NAME_MAX_LENGTH)).optional(),
    // FIX-1211 M3 (security): tên ngân hàng gõ tự do, đứng cạnh số TK ⇒ cùng chốt nhãn người nhận (`payee-label.ts`).
    bankName: payeeLabelSchema((s) => s.min(1, BANK_NAME_REQUIRED_MESSAGE).max(BANK_NAME_MAX_LENGTH)).optional(),
  })
  .strict();

export type GroupBankAccountRequest = z.infer<typeof groupBankAccountSchema>;

/**
 * `memberDueIds=<uuid>,<uuid>,…` — tối đa 50, KHÔNG trùng. Dạng chuỗi phân cách dấu phẩy vì đây là
 * query string của một GET (mảng lặp khoá `?a=&a=` bị `.strict()` + parser query của Nest đọc thành
 * nhiều kiểu khác nhau tuỳ số phần tử).
 */
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}";
const memberDueIdsCsvSchema = z
  .string()
  .regex(new RegExp(`^${UUID}(,${UUID}){0,49}$`), "memberDueIds phải là danh sách uuid phân cách dấu phẩy")
  // Chuẩn hoá chữ thường TRƯỚC khi kiểm trùng (review #780 · M3 — Postgres trả uuid chữ thường).
  .transform((csv) => csv.toLowerCase())
  .refine((csv) => {
    const ids = csv.split(",");
    return new Set(ids).size === ids.length;
  }, "memberDueIds có phần tử trùng");

/**
 * Query cho `GET /v1/groups/:id/bank-account`. `memberDueId` tuỳ chọn ⇒ kèm `transferContent`.
 * `.strict()` — query lạ ⇒ 400 tại biên, không im lặng bỏ qua.
 *
 * 🔴 US-E6.F8-0784 — `memberDueIds` (nhiều kỳ, một lần chuyển): nội dung CK dựng cho CẢ TẬP —
 * cùng một lịch ⇒ `"<tên đợt> K3-K5"` (liền) / `"<tên đợt> K3 K5"` (rời), khác lịch ⇒ nối tên các
 * đợt — rồi vẫn đi qua `buildTransferContent` (một nguồn cắt/không dấu). Gửi CẢ HAI field ⇒ 400: hai
 * cách nói cùng một điều là hai cách để lệch nhau.
 */
export const groupBankAccountQuerySchema = z
  .object({ memberDueId: z.string().uuid().optional(), memberDueIds: memberDueIdsCsvSchema.optional() })
  .strict()
  .refine((q) => !(q.memberDueId && q.memberDueIds), "Chỉ gửi một trong memberDueId / memberDueIds");

export type GroupBankAccountQueryInput = z.infer<typeof groupBankAccountQuerySchema>;

/**
 * Hình dạng LƯU trong `Group.bankAccount` jsonb — **nguồn sự thật của những khoá được phép tồn tại**.
 *
 * 🔴 `bankBin` có mặt ở đây nhưng KHÔNG có trong `groupBankAccountSchema`: nó chỉ vào được qua đường
 * quỹ cũ (flutter-web). Khai ở đây để `_merge` biết **phải giữ nó lại**; thiếu khai ⇒ lần ghi đầu từ
 * mobile sẽ thổi bay `bankBin` mà không ai thấy.
 *
 * `qrFileName`/`qrSizeBytes` là **yêu cầu của thiết kế** (§9 C3): màn Cài đặt hiện *"qr_code_….png ·
 * 240 KB"* mỗi lần mở lại, nên hai giá trị này phải SỐNG QUA reload, không phải state tạm của client.
 * ⚠️ `qrFileName` do người dùng đặt và có thể chứa tên người ⇒ **KHÔNG đưa vào audit diff**.
 */
export const storedGroupBankAccountSchema = z
  .object({
    bankBin: z.string().optional(),
    accountNumber: z.string().optional(),
    accountName: z.string().optional(),
    bankName: z.string().optional(),
    qrImageKey: z.string().optional(),
    qrFileName: z.string().max(QR_FILE_NAME_MAX_LENGTH).optional(),
    // Số BYTE, không phải tiền — an toàn để là number (§3.1 chỉ cấm float cho TIỀN).
    qrSizeBytes: z.number().int().nonnegative().optional(),
  })
  .strict();

export type StoredGroupBankAccount = z.infer<typeof storedGroupBankAccountSchema>;

/**
 * Response cho `GET` và `PUT /v1/groups/:id/bank-account`.
 *
 * 🔴 VÌ SAO CẦN MỘT ĐƯỜNG ĐỌC RIÊNG (cái mà cơ chế cũ KHÔNG có): `GET /v1/funds/:id/qr` **ném 409 khi
 * chưa upload QR** (`fund-bank.service.ts:168`). Với màn Cài đặt thì đó là sai bài toán — O/A vừa gõ
 * số tài khoản, chưa kịp up QR, mở lại màn thì **không đọc lại được gì** và tưởng app nuốt mất dữ
 * liệu. Đường này trả 200 với các field vắng mặt, KHÔNG 409.
 *
 * `qrImageUrl` là URL presigned **TTL hữu hạn** — KHÔNG lưu lại ở client, mở lại màn thì xin lại.
 */
export const groupBankAccountResponseSchema = z
  .object({
    bankBin: z.string().optional(),
    accountNumber: z.string().optional(),
    accountName: z.string().optional(),
    bankName: z.string().optional(),
    qrImageUrl: z.string().optional(),
    qrFileName: z.string().optional(),
    qrSizeBytes: z.number().int().nonnegative().optional(),
    /**
     * 🔴 US-E5.F2-0598-S07 (@UC0057) — nội dung CK gợi ý cho **một khoản cụ thể**, chỉ có mặt khi
     * client truyền `memberDueId`. Màn Cài đặt (O/A) không truyền ⇒ vắng field này, đúng: ở đó không
     * có "khoản" nào để sinh nội dung.
     *
     * ⚠️ Đây là GỢI Ý CHO CON NGƯỜI, không phải mã đối chiếu: người dùng sửa được nó trong app ngân
     * hàng và vẫn có thể quên dán. Sổ sách KHÔNG BAO GIỜ khớp tiền bằng chuỗi này.
     */
    transferContent: z.string().nullable().optional(),
    /**
     * `true` ⇒ nhóm CHƯA có ảnh QR. **QĐ-3**: chỉ ảnh QR mới tính là đã cấu hình; gõ đủ ba ô text mà
     * không up QR vẫn là `true`. Giữ đúng luật `needsConfig` cũ (dựa `qrImageKey`) để `flutter-web`
     * và DTO mobile hiện có không phải sửa một dòng nào.
     */
    needsConfig: z.boolean(),
  })
  .strict();

export type GroupBankAccountResponse = z.infer<typeof groupBankAccountResponseSchema>;

/** Response cho `PUT /v1/groups/:id/qr-image` — đủ để client dựng NGAY khối "đã fill" của thiết kế. */
export const groupQrUploadResponseSchema = z
  .object({
    qrImageUrl: z.string().min(1),
    qrFileName: z.string(),
    qrSizeBytes: z.number().int().nonnegative(),
  })
  .strict();

export type GroupQrUploadResponse = z.infer<typeof groupQrUploadResponseSchema>;

/**
 * Mã lỗi ổn định, ngôn-ngữ-trung-tính (`CLAUDE.md §7`) — client map `code → message`.
 * `detail` chỉ để debug, **không phải nguồn hiển thị**.
 */
export const GROUP_BANK_CODE = {
  /** Xoá/đọc QR khi nhóm chưa từng cấu hình. */
  QR_NOT_CONFIGURED: "GROUP_BANK_QR_NOT_CONFIGURED",
  /** Tệp tải lên không phải ảnh nhận được (magic-byte / content-type). */
  QR_INVALID_IMAGE: "GROUP_BANK_QR_INVALID_IMAGE",
  /** Ảnh vượt trần dung lượng. */
  QR_TOO_LARGE: "GROUP_BANK_QR_TOO_LARGE",
} as const;

export type GroupBankCode = (typeof GROUP_BANK_CODE)[keyof typeof GROUP_BANK_CODE];
